import { noop } from 'lodash-es';
import { memo } from 'react';

import { Action } from '../../types/Action';
import { ButtonSize } from '../../types/SharedButtonProps';
import { SharedIconProps } from '../../types/SharedIconProps';
import { ActionsMenu } from '../ActionsMenu/ActionsMenu';
import { Button } from '../Button/Button';
import { IconButton } from '../Button/subcomponents/IconButton';
import { resolveButtonIconSize } from '../Button/utils';
import { Layout } from '../Layout/Layout';

interface Props {
  actions: Action[];
  mode?: 'icon';
  moreActions?: {
    actions: Action[];
    icon: SharedIconProps;
  };
  size?: ButtonSize;
}

export const Actions = memo(
  ({ actions, mode, moreActions, size = 'm' }: Props) => {
    return (
      <Layout preset="buttons" role="toolbar" spacing={size}>
        {actions.map((action) => {
          const usesToggle = action.onToggle !== undefined;
          const tooltip = {
            direction: action.tooltip?.direction,
            text:
              action.tooltip?.text ??
              (mode === 'icon' ? action.label : undefined),
          };

          if (mode === 'icon') {
            if (!action.icon) {
              return null;
            }

            return (
              <IconButton
                aria-current={action.current ? true : undefined}
                disabled={action.disabled}
                icon={action.icon}
                isLoading={action.isLoading}
                key={action.label}
                label={action.label}
                selected={usesToggle ? (action.selected ?? false) : undefined}
                size={size}
                status={usesToggle ? action.status : undefined}
                tooltip={tooltip}
                variant="tertiary"
                onClick={usesToggle ? undefined : (action.onClick ?? noop)}
                onToggle={usesToggle ? action.onToggle : undefined}
              />
            );
          }

          return (
            <Button
              disabled={action.disabled}
              icon={action.icon}
              isLoading={action.isLoading}
              key={action.label}
              label={action.label}
              selected={usesToggle ? (action.selected ?? false) : undefined}
              size={size}
              status={usesToggle ? action.status : undefined}
              text={action.text}
              tooltip={tooltip}
              variant={action.variant}
              onClick={usesToggle ? undefined : (action.onClick ?? noop)}
              onToggle={usesToggle ? action.onToggle : undefined}
            />
          );
        })}
        {moreActions && (
          <ActionsMenu
            {...moreActions}
            icon={{
              ...moreActions.icon,
              size: resolveButtonIconSize({ isIconMode: true, size }),
            }}
            size={size}
          />
        )}
      </Layout>
    );
  },
);

Actions.displayName = 'Actions';
